import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trackingHealth } from "../../../helpers/api/tracking-health";
import { ShowToast } from "../../../components/toast";
import { normalizeCustomerId } from "./hooks/useOnboardingPipelines";
import { thKeys } from "./hooks/useTrackingHealth";
import type { OnboardingMergedRow } from "./onboarding-merge";
import type {
  OnboardingHiddenRow,
  OnboardingHiddenRowsPayload,
} from "./types";

/**
 * Hidden onboarding rows — an operator judgement ("stuck indefinitely",
 * "churned mid-onboarding") that neither system this section joins can state
 * for itself, so Tracking Health stores it: `tracking_onboarding_hidden_row`,
 * behind `GET/POST admin/tracking-health/onboarding/hidden-rows`.
 *
 * It was this browser's `localStorage` until 2026-08-17, which made a fleet
 * judgement a per-machine one: a second operator saw a census the first had
 * already triaged, every new laptop re-triaged it, and clearing site data
 * silently reset the section. It is now shared across admins and stamped with
 * who made the call. Rows are still never deleted, only filtered — the section
 * header offers "Show hidden (n)", each shown-hidden row an Unhide, and the
 * KPI tile carries `+n hidden`.
 *
 * A row matches a stored judgement on EITHER its key or its joined customer
 * id, because the merged table keys a row by the platform `userId` while it
 * has a pipeline and by the customer id when it does not — a pipeline arriving
 * late (or graduating) changes the key of a row that is the same hotel.
 *
 * Failure is fail-open by construction: if the read fails the section shows
 * every row rather than hiding rows it cannot account for. A failed WRITE is
 * different — it rolls the optimistic change back and says so, since a hide
 * that silently did not persist is what this replaced.
 */

/** The pre-2026-08-17 local list, drained into the server once and removed. */
const STORAGE_KEY = "tracking-health.hidden-onboarding-rows";

/**
 * Why the write did not land, in the operator's own terms.
 *
 * The first cut said only "it is still in the census", which is the one thing
 * the reader can already see. The distinction that matters is whether the API
 * *refused* the write or was never reached at all: axios sets `response` only
 * in the first case, so a missing one means the request never got an answer —
 * the API this portal points at (`REACT_APP_API_URL`) is not up, or the browser
 * could not reach it. A 404 is its own answer: the backend serving this portal
 * predates the durable list and is still the localStorage-era build.
 */
function whyFailed(error: any): string {
  if (!error?.response) {
    return "the API didn't answer (is the one this portal points at running?)";
  }
  const status = error.response.status;
  if (status === 404) {
    return "this API has no hidden-row store yet (404) — it is running an older build";
  }
  const detail = error.response.data?.message;
  return detail ? `the server refused it: ${detail}` : `the server answered ${status}`;
}

export interface HiddenOnboardingRows {
  /** Is this row currently hidden (by key or by joined customer id)? */
  isHidden: (row: OnboardingMergedRow) => boolean;
  /** The judgement behind a hidden row — who hid it, when — or null. */
  hiddenInfo: (row: OnboardingMergedRow) => OnboardingHiddenRow | null;
  toggleHidden: (row: OnboardingMergedRow) => void;
  /** The read failed: nothing is hidden, and the section says why. */
  isError: boolean;
}

/** The customer id a row would be matched by, digits-only; null when the row
 *  has no Ads account on either side yet. */
function rowCustomerId(row: OnboardingMergedRow): string | null {
  return normalizeCustomerId(
    row.hotel?.customerId ?? row.user?.googleAdsCustomerId,
  );
}

export function useHiddenOnboardingRows(): HiddenOnboardingRows {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: thKeys.onboardingHidden,
    queryFn: trackingHealth.onboardingHiddenRows,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const rows = useMemo(() => query.data?.rows ?? [], [query.data]);

  /** Both lookups the match rule needs, built once per read. */
  const index = useMemo(() => {
    const byKey = new Map<string, OnboardingHiddenRow>();
    const byCustomerId = new Map<string, OnboardingHiddenRow>();
    for (const row of rows) {
      byKey.set(row.rowKey, row);
      const cid = normalizeCustomerId(row.customerId);
      if (cid) byCustomerId.set(cid, row);
    }
    return { byKey, byCustomerId };
  }, [rows]);

  const matchOf = useCallback(
    (row: OnboardingMergedRow): OnboardingHiddenRow | null => {
      const byKey = index.byKey.get(row.key);
      if (byKey) return byKey;
      const cid = rowCustomerId(row);
      return (cid && index.byCustomerId.get(cid)) || null;
    },
    [index],
  );

  const writeCache = useCallback(
    (next: (current: OnboardingHiddenRow[]) => OnboardingHiddenRow[]) => {
      qc.setQueryData<OnboardingHiddenRowsPayload>(
        thKeys.onboardingHidden,
        (prev) => ({ rows: next(prev?.rows ?? []) }),
      );
    },
    [qc],
  );

  /** Roll the list back to what it was before the optimistic edit, and say so
   *  — a hide that did not persist must not keep looking like one. */
  const rollback = useCallback(
    (
      previous: OnboardingHiddenRowsPayload | undefined,
      what: string,
      error: any,
    ) => {
      if (previous) qc.setQueryData(thKeys.onboardingHidden, previous);
      else qc.invalidateQueries({ queryKey: thKeys.onboardingHidden });
      ShowToast({ type: "error", message: `${what} — ${whyFailed(error)}` });
    },
    [qc],
  );

  const hide = useMutation({
    mutationFn: (vars: { rowKey: string; customerId: string | null }) =>
      trackingHealth.hideOnboardingRow(vars.rowKey, {
        customerId: vars.customerId,
      }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: thKeys.onboardingHidden });
      const previous = qc.getQueryData<OnboardingHiddenRowsPayload>(
        thKeys.onboardingHidden,
      );
      writeCache((current) => [
        ...current.filter((row) => row.rowKey !== vars.rowKey),
        {
          rowKey: vars.rowKey,
          customerId: vars.customerId,
          hiddenAt: new Date().toISOString(),
          // Replaced by the server's own stamp on the settle refetch.
          hiddenBy: "",
          reason: null,
        },
      ]);
      return { previous };
    },
    onError: (error, _vars, context) =>
      rollback(context?.previous, "That row is still in the census", error),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: thKeys.onboardingHidden }),
  });

  const unhide = useMutation({
    mutationFn: (vars: { rowKey: string }) =>
      trackingHealth.unhideOnboardingRow(vars.rowKey),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: thKeys.onboardingHidden });
      const previous = qc.getQueryData<OnboardingHiddenRowsPayload>(
        thKeys.onboardingHidden,
      );
      writeCache((current) =>
        current.filter((row) => row.rowKey !== vars.rowKey),
      );
      return { previous };
    },
    onError: (error, _vars, context) =>
      rollback(context?.previous, "That row is still hidden", error),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: thKeys.onboardingHidden }),
  });

  const toggleHidden = useCallback(
    (row: OnboardingMergedRow) => {
      const match = matchOf(row);
      // Unhide targets the STORED key, which is not always this row's key —
      // matching on the customer id is exactly the case where they differ.
      if (match) unhide.mutate({ rowKey: match.rowKey });
      else hide.mutate({ rowKey: row.key, customerId: rowCustomerId(row) });
    },
    [matchOf, hide, unhide],
  );

  useLocalListMigration(query.isSuccess, rows, hide.mutateAsync, qc);

  return {
    isHidden: useCallback((row) => matchOf(row) !== null, [matchOf]),
    hiddenInfo: matchOf,
    toggleHidden,
    isError: query.isError,
  };
}

/**
 * One-time drain of the pre-2026-08-17 local list into the shared one.
 *
 * Whatever this browser had hidden is a judgement somebody made; dropping it
 * on upgrade would put every one of those rows back in the census with no
 * trace of why they left it. Keys the server already knows are skipped, the
 * storage key is removed either way once the push succeeds, and a failure
 * leaves the local list intact to retry on the next load.
 */
function useLocalListMigration(
  loaded: boolean,
  rows: OnboardingHiddenRow[],
  hideRow: (vars: {
    rowKey: string;
    customerId: string | null;
  }) => Promise<unknown>,
  qc: ReturnType<typeof useQueryClient>,
): void {
  const done = useRef(false);

  useEffect(() => {
    if (!loaded || done.current) return;
    done.current = true;

    let local: string[] = [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      local = Array.isArray(parsed)
        ? parsed.filter((v): v is string => typeof v === "string" && !!v.trim())
        : [];
    } catch {
      return; // Unreadable storage — nothing to migrate, nothing to clear.
    }
    if (local.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }

    const known = new Set(rows.map((row) => row.rowKey));
    const missing = local.filter((key) => !known.has(key));

    void (async () => {
      try {
        // Sequential on purpose: this runs once, over a handful of keys, and
        // a burst of writes at page load buys nothing.
        for (const rowKey of missing) {
          await hideRow({ rowKey, customerId: null });
        }
        window.localStorage.removeItem(STORAGE_KEY);
        qc.invalidateQueries({ queryKey: thKeys.onboardingHidden });
        if (missing.length > 0) {
          ShowToast({
            type: "success",
            message: `Moved ${missing.length} hidden onboarding row${
              missing.length === 1 ? "" : "s"
            } from this browser to the shared list`,
          });
        }
      } catch {
        // Keep the local list for the next load rather than losing it.
        done.current = false;
      }
    })();
  }, [loaded, rows, hideRow, qc]);
}
